import {
  getPublicErrorPayload,
  type PublicErrorPayload,
} from '../../utils/apollo/apolloErrorMessage'

// Cap free-form details text so a long backend string can't bloat the RUM error
// title (and, transitively, the default grouping).
const MAX_DETAILS_TEXT = 200

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Splits the sanctioned `public_error_details` channel into the two pieces we
 * report differently:
 *
 *   - `code`: a low-cardinality, enum-like identifier. Many backends (e.g.
 *     shifts) nest the fine-grained error code at `public_error_details.code`.
 *     Safe for both the RUM message title AND the Datadog fingerprint.
 *   - `text`: a bare string payload (legacy shape). Informative for the message,
 *     but kept OUT of the fingerprint since free-form text is high-cardinality
 *     and would fragment grouping.
 */
const extractDetailsParts = (details: unknown): { code?: string; text?: string } => {
  if (typeof details === 'string') {
    const trimmed = details.trim()
    return trimmed ? { text: trimmed.slice(0, MAX_DETAILS_TEXT) } : {}
  }
  if (isRecord(details) && typeof details.code === 'string' && details.code) {
    return { code: details.code }
  }
  return {}
}

export interface GraphQLErrorReportDetails {
  /**
   * The sanctioned client-safe payload (`public_error_*`) for attaching to the
   * RUM error context, or `undefined` when the backend didn't populate it.
   */
  publicError: PublicErrorPayload | undefined
  /**
   * Space-joined discriminators to append to the human-readable RUM message,
   * e.g. ` [DRIVER_INACTIVE]`. Empty string when there's nothing to add.
   */
  messageSuffix: string
  /**
   * Underscore-joined discriminators to append to the Datadog `dd_fingerprint`,
   * e.g. `_DRIVER_INACTIVE`. Empty string when there's nothing to add, so
   * errors without a public payload keep their existing fingerprint bucket.
   */
  fingerprintSuffix: string
}

/**
 * Derives extra reporting signal from the mesh's sanctioned public-error channel
 * (`public_error_code` / `error_types` / `public_error_details`).
 *
 * The mesh masks internal error messages to a generic `"An error has occurred."`,
 * so a single (`extensions.code`, `operationName`) pair rolls up many distinct
 * backend failures in RUM/Datadog. This surfaces the distinguishing fields into
 * the reported message, the fingerprint, and the error context so on-call can
 * tell those failures apart. See `getPublicErrorPayload` and the IPP Error
 * Handling Strategy for the channel contract.
 *
 * `extensionsCode` is the top-level `extensions.code` the message already carries
 * (passed so we can drop a redundant `public_error_code` that merely echoes it).
 */
export const getGraphQLErrorReportDetails = (
  graphQLError: unknown,
  extensionsCode?: unknown
): GraphQLErrorReportDetails => {
  const publicError = getPublicErrorPayload(graphQLError)

  const codeStr = extensionsCode == null ? undefined : String(extensionsCode)
  const publicCode = publicError?.public_error_code
  const { code: detailsCode, text: detailsText } = extractDetailsParts(
    publicError?.public_error_details
  )

  // Enum-like discriminators, safe for both the message and the fingerprint.
  const fingerprintTokens = [
    // Include the public code only when it adds signal beyond extensions.code,
    // which the message/fingerprint already carry.
    publicCode && publicCode !== codeStr ? publicCode : undefined,
    ...(publicError?.error_types ?? []),
    detailsCode,
  ].filter((token): token is string => Boolean(token))

  // The message additionally carries free-form details text (kept out of the
  // fingerprint to avoid fragmenting grouping).
  const messageTokens = [...fingerprintTokens, detailsText].filter((token): token is string =>
    Boolean(token)
  )

  return {
    publicError,
    messageSuffix: messageTokens.length ? ` [${messageTokens.join(' ')}]` : '',
    fingerprintSuffix: fingerprintTokens.length
      ? `_${fingerprintTokens.map(token => token.replace(/\s+/g, '-')).join('_')}`
      : '',
  }
}
